import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import "./CaseAdminFilters.css";

import { Case, Lab } from "pwapi";

import { fetchAdminCases } from "shared/actions/admin/adminCasesActions";
import { getLabs } from "shared/actions/labActions";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import { OptionType } from "components/customInputs/MultiColumnSelectInput";
import {
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetBooleanFilters,
  resetFilters,
} from "components/filtersUtils";
import { useAppSelector } from "store/hooks";

import CaseFilters, { qaReviewLabels } from "../filters/CaseFilters";
import DateRangeFilters from "../filters/DateRangeFilters";
import HiddenDropDown from "../filters/HiddenDropdown";
import SlidesFilters from "../filters/SlidesFilters";
import SpecimenFilters from "../filters/SpecimenFilters";
import {
  Filter,
  FilterPageFunctionComponentProps,
  QueryFilter,
} from "../types/adminTypes";

type FiltersObj = {
  case: Filter;
  dateRange: Filter;
  slide: Filter;
  specimen: Filter;
  query: QueryFilter;
};

type FilterValue = string | OptionType[];

const CaseAdminFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  toggleAllRowsExpanded,
  setPage,
}: FilterPageFunctionComponentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);

  const labsState = useAppSelector((state) => state.labs);
  const labs =
    labsState?.results &&
    [...labsState?.results].sort((labA: Lab, labB: Lab) =>
      labA.name > labB.name ? 1 : -1
    );

  useEffect(() => {
    !labsState && dispatch(getLabs({ limit: 9999, offset: 0 }));
  }, []);

  const usp = new URLSearchParams(location.search);

  const initializeFilters = (filters: any) => {
    return Object.keys(filters).reduce<Record<string, FilterValue>>(
      (acc, value) => {
        const initialFilter = usp.get(value);
        acc[value] = initialFilter
          ? value === "qaReviewHold"
            ? initialFilter.split(",").map((reviewFilter: string) => ({
                label:
                  qaReviewLabels[reviewFilter as keyof typeof qaReviewLabels],
                value: reviewFilter,
              }))
            : initialFilter
          : "";
        return acc;
      },
      {}
    );
  };

  const [slideFilters, setSlideFilters] = useState<any>(
    initializeFilters({
      slidesStatus: "",
      slidesStainName: "",
      slidesLab: "",
      slidesSlideReviewReason: "",
      slidesQcStatus: "",
      slidesScannedDateAfter: "",
      slidesScannedDateBefore: "",
      slidesExpectedUploadByAfter: "",
      slidesExpectedUploadByBefore: "",
    })
  );

  const [specimenFilters, setSpecimenFilters] = useState<any>(
    initializeFilters({
      specimensBiopsyType: "",
      specimensProcedure: "",
      specimensType: "",
      specimensSource: "",
      specimensSite: "",
      grossCompletedAfter: "",
      grossCompletedBefore: "",
    })
  );

  const [caseFilters, setCaseFilters] = useState<any>(
    initializeFilters({
      status: "",
      accession: "",
      accessioningLab: "",
      type: "",
      source: "",
      activeAssignmentName: "",
      patientName: "",
      patientDateOfBirth: "",
      patientClinicName: "",
      patientClinicClinicGroupName: "",
      patientClinicSonicPracticeName: "",
      patientAddressState: "",
      patientClinicLocationState: "",
      patientInsuranceInsuranceCompanyName: "",
      orderingDoctorName: "",
      grossedByCert: "",
      billingType: "",
      qaReviewHold: [],
      billerName: "",
      clinicNumber: "",
    })
  );

  const [dateRangeFilters, setDateRangeFilters] = useState<any>(
    initializeFilters({
      receivedDateAfter: "",
      receivedDateBefore: "",
      collectedDateAfter: "",
      collectedDateBefore: "",
      accessionCompletedAfter: "",
      accessionCompletedBefore: "",
      finalizedAtAfter: "",
      finalizedAtBefore: "",
    })
  );

  const [queryFilters, setQueryFilters] = useState<{
    [key: string]: boolean | undefined;
  }>({
    isDiagnostic: !!usp.get("isDiagnostic"),
    reviewed: !!usp.get("reviewed"),
    dataEntry: usp.get("dataEntry")
      ? usp.get("dataEntry") === "true"
        ? true
        : usp.get("dataEntry") === "false"
        ? false
        : undefined
      : undefined,
    billingEntry: usp.get("billingEntry")
      ? usp.get("billingEntry") === "true"
        ? true
        : usp.get("billingEntry") === "false"
        ? false
        : undefined
      : undefined,
    hasFinalizingSignature: !!usp.get("hasFinalizingSignature"),
    unviewed: !!usp.get("unviewed"),
    selfPay: !!usp.get("selfPay"),
    isManuallyAccessioned: usp.get("isManuallyAccessioned")
      ? usp.get("isManuallyAccessioned") === "true"
        ? true
        : false
      : undefined,
  });

  // A variable to reference if the component has just mounted
  // This is intended for use to block the first case list api call on first render.
  const didMount = useRef<boolean>(false);

  useEffect(() => {
    // Do not set the search params if default params are true and the filters have just mounted
    // This is to prevent changing the location object and causing a second render
    if (didMount.current || sortBy[0]?.id !== "priority" || pageIndex !== 0)
      setSearchParams({
        case: caseFilters,
        dateRange: dateRangeFilters,
        slide: slideFilters,
        specimen: specimenFilters,
        query: queryFilters,
      });
  }, [sortBy, pageIndex]);

  useEffect(() => {
    const filtersAreEmpty = [
      ...Object.values(caseFilters),
      ...Object.values(dateRangeFilters),
      ...Object.values(slideFilters),
      ...Object.values(specimenFilters),
    ].every((value) => value === "");

    // Do not run if all the defaults are true and it just mounted for the first time.
    // If the filters are empty, the list is in its default state.
    if (
      !filtersAreEmpty ||
      (sortBy?.length > 0 && sortBy[0].id !== "priority") ||
      pageIndex !== 0 ||
      didMount.current
    ) {
      filterCases({
        case: caseFilters,
        dateRange: dateRangeFilters,
        slide: slideFilters,
        specimen: specimenFilters,
        query: queryFilters,
      });
    }

    didMount.current = true;
  }, [sortBy, pageIndex, pageSize]);

  const onChangeSlideFilter = (key: string, value: string) => {
    setSlideFilters({ ...slideFilters, [key]: value });
  };

  const onChangeSpecimenFilter = (key: string, value: string) => {
    setSpecimenFilters({ ...specimenFilters, [key]: value });
  };

  const onChangeCaseFilter = (key: string, value: string | string[]) => {
    setCaseFilters({ ...caseFilters, [key]: value });
  };

  const onChangeSpecimenDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(specimenFilters, setSpecimenFilters, values);
  };

  const onChangeSlideDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(slideFilters, setSlideFilters, values);
  };

  const onChangeDateRangeFilters = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(dateRangeFilters, setDateRangeFilters, values);
  };

  const onChangeQueryFilter = (key: string) => {
    setQueryFilters({ ...queryFilters, [key]: !queryFilters[key] });
  };

  const onChangeQueryFilterControlled = (key: string, value?: boolean) => {
    setQueryFilters({ ...queryFilters, [key]: value });
  };

  const onChangeDateRangeFilter = (
    filters: string[],
    set: Function,
    values: { [key: string]: Date }
  ) => {
    const newState = Object.assign({}, filters, values);
    set(newState);
  };

  const reduceQueriesToApi = (filters: QueryFilter) => {
    return Object.keys(filters).reduce((acc: QueryFilter, filter: string) => {
      if (
        (filter === "isManuallyAccessioned" ||
          filter === "dataEntry" ||
          filter === "billingEntry") &&
        filters[filter] !== undefined
      ) {
        acc[filter] = filters[filter];
      } else if (filters[filter]) {
        acc[filter] =
          filter === "reviewed" || filter === "hasFinalizingSignature"
            ? false
            : true;
      }
      return acc;
    }, {});
  };

  const setSearchParams = (filters: FiltersObj) => {
    const params = {
      ...reduceFiltersForSearchParams(filters.case),
      ...reduceFiltersForSearchParams(filters.dateRange),
      ...reduceFiltersForSearchParams(filters.slide),
      ...reduceFiltersForSearchParams(filters.specimen),
      ...reduceQueriesToApi(filters.query),
    } as any;
    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterCases = (filters: FiltersObj, resetPage?: boolean) => {
    const grossedByCertFilter =
      (filters.case.grossedByCert as string)?.match(/,(\d*)$/)?.[1] ||
      undefined;
    const source =
      filters.query.reviewed || filters.query.hasFinalizingSignature
        ? Case.source.PW
        : filters.case.source;
    const payload = {
      limit: pageSize,
      offset: resetPage ? 0 : pageSize * pageIndex,
      ordering: [
        ...sortBy.map((s) => {
          const descending = s.desc ? "-" : "";
          return s.id === "priority"
            ? "-priority,-patient__clinic__tat_priority,received_date"
            : `${descending}${s.id}`;
        }),
      ].join(","),
      ...reduceFiltersToApi(filters.case),
      ...reduceFiltersToApi(filters.dateRange),
      ...reduceFiltersToApi(filters.slide),
      ...reduceFiltersToApi(filters.specimen),
      ...reduceQueriesToApi(filters.query),
      ...(grossedByCertFilter && { grossedByCert: grossedByCertFilter }),
      ...(source && { source: source }),
    };
    toggleAllRowsExpanded && toggleAllRowsExpanded(false);
    resetPage && pageIndex !== 0
      ? setPage(0)
      : dispatch(
          fetchAdminCases({
            filters: payload,
            isManualReview: filters.slide?.slidesStatus === "manual-review",
          })
        );
  };

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchParams({
      case: caseFilters,
      dateRange: dateRangeFilters,
      slide: slideFilters,
      specimen: specimenFilters,
      query: queryFilters,
    });
    filterCases(
      {
        case: caseFilters,
        dateRange: dateRangeFilters,
        slide: slideFilters,
        specimen: specimenFilters,
        query: queryFilters,
      },
      true
    );
  };

  const onResetFilter = () => {
    const filters = {
      case: resetFilters(caseFilters),
      dateRange: resetFilters(dateRangeFilters),
      slide: resetFilters(slideFilters),
      specimen: resetFilters(specimenFilters),
      query: resetBooleanFilters(queryFilters),
    };
    setSlideFilters(filters.slide);
    setCaseFilters(filters.case);
    setDateRangeFilters(filters.dateRange);
    setSpecimenFilters(filters.specimen);
    setQueryFilters(filters.query);
    setSearchParams(filters);
  };

  return (
    <form className="AdminFilters" onSubmit={onSubmitFilter}>
      <div className="AdminFilters AdminFilters--vertical">
        <div className="AdminFilters__filters">
          <CaseFilters
            {...caseFilters}
            labs={labs}
            isManuallyAccessioned={queryFilters.isManuallyAccessioned}
            onChange={onChangeCaseFilter}
            onChangeManuallyAccessioned={onChangeQueryFilterControlled}
          />
        </div>
        <HiddenDropDown
          label="Case Date Filters"
          type="filter"
          isInitOpen={true}
        >
          <div className="AdminFilters__filters">
            <DateRangeFilters
              {...dateRangeFilters}
              onChangeDateRange={onChangeDateRangeFilters}
            />
          </div>
        </HiddenDropDown>
        <HiddenDropDown
          label="Specimen Filters"
          type="filter"
          isInitOpen={true}
        >
          <div className="AdminFilters__filters">
            <SpecimenFilters
              {...specimenFilters}
              onChange={onChangeSpecimenFilter}
              onChangeDateRange={onChangeSpecimenDateRangeFilter}
            />
          </div>
        </HiddenDropDown>
        <HiddenDropDown label="Slide Filters" type="filter" isInitOpen={true}>
          <div className="AdminFilters__filters">
            <SlidesFilters
              {...slideFilters}
              labs={labs}
              onChange={onChangeSlideFilter}
              onChangeDateRange={onChangeSlideDateRangeFilter}
            />
          </div>
        </HiddenDropDown>
      </div>
      <div className="AdminFilters__actions AdminFilters__actions--content">
        <div className="AdminFilters__actions--content">
          <ControlledToggleButton
            isToggled={!!queryFilters.isDiagnostic}
            onClick={() => {
              onChangeQueryFilter("isDiagnostic");
            }}
          >
            Diagnostic Cases
          </ControlledToggleButton>
          {features?.qaReviews && (
            <ControlledToggleButton
              isToggled={!!queryFilters.reviewed}
              onClick={() => {
                setQueryFilters({
                  ...queryFilters,
                  dataEntry: false,
                  reviewed: queryFilters.reviewed ? false : true,
                  billingEntry: undefined,
                });
              }}
            >
              Unreviewed Cases
            </ControlledToggleButton>
          )}
          <ControlledToggleButton
            isToggled={!!queryFilters.dataEntry}
            onClick={() => {
              setQueryFilters({
                ...queryFilters,
                dataEntry: queryFilters.dataEntry ? false : true,
                reviewed: undefined,
                billingEntry: undefined,
              });
            }}
          >
            Needing Data Entry
          </ControlledToggleButton>
          {features?.billingEntry && (
            <>
              <ControlledToggleButton
                isToggled={queryFilters.billingEntry === true}
                onClick={() => {
                  setQueryFilters({
                    ...queryFilters,
                    dataEntry: undefined,
                    reviewed: undefined,
                    billingEntry: queryFilters.billingEntry ? undefined : true,
                  });
                }}
              >
                Needing Billing Entry
              </ControlledToggleButton>
              <ControlledToggleButton
                isToggled={queryFilters.billingEntry === false}
                onClick={() => {
                  setQueryFilters({
                    ...queryFilters,
                    dataEntry: undefined,
                    reviewed: undefined,
                    billingEntry: false,
                  });
                }}
              >
                Billing Entry Holds
              </ControlledToggleButton>
            </>
          )}
          <ControlledToggleButton
            isToggled={!!queryFilters.hasFinalizingSignature}
            onClick={() => {
              onChangeQueryFilter("hasFinalizingSignature");
            }}
          >
            Missing Finalizing Signature
          </ControlledToggleButton>
          <ControlledToggleButton
            isToggled={!!queryFilters.unviewed}
            onClick={() => {
              onChangeQueryFilter("unviewed");
            }}
          >
            Unviewed Cases
          </ControlledToggleButton>
          <ControlledToggleButton
            isToggled={!!queryFilters.selfPay}
            onClick={() => {
              onChangeQueryFilter("selfPay");
            }}
          >
            Self Pay Cases
          </ControlledToggleButton>
        </div>
        <div className="AdminFilters__actions--content">
          <PathologyWatchButton
            type="submit"
            color="green"
            title="filter cases"
          >
            Filter Cases
          </PathologyWatchButton>
          <PathologyWatchButton onClick={onResetFilter} title="reset filters">
            Reset Filters
          </PathologyWatchButton>
        </div>
      </div>
    </form>
  );
};

export default CaseAdminFilters;
